import { supabase } from '../supabase';
import { handleError, checkWorkspaceAccess } from './db-utils';

/**
 * Get workspace overview data
 */
export const getWorkspaceOverview = async (workspaceId: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Use the shared access helper so superadmins assuming a role on a
    // workspace they're not a member of can still load the overview. The
    // inline workspace_users lookup we used to do here had no superadmin
    // bypass, so the dashboard's Promise.all rejected and the page rendered
    // its empty-state even though the sidebar (which uses checkWorkspaceAccess
    // via getWorkspaceProjects) showed the projects fine.
    const hasAccess = await checkWorkspaceAccess(workspaceId);
    if (!hasAccess) {
      throw new Error('Access denied to workspace');
    }

    // Project counts exclude drafts so the totals are consistent across
    // workspace members (drafts are creator-only, but workspace overviews
    // are shared metrics).
    const { count: totalProjects } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .neq('status', 'draft');

    // Get projects by health status (drafts excluded for the same reason)
    const { count: onTrackProjects } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('overall_health', 'green')
      .neq('status', 'draft');

    const { count: atRiskProjects } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('overall_health', 'yellow')
      .neq('status', 'draft');

    const { count: offTrackProjects } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('overall_health', 'red')
      .neq('status', 'draft');

    // Get user count
    const { count: totalUsers } = await supabase
      .from('workspace_users')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId);

    // Get admin count
    const { count: adminUsers } = await supabase
      .from('workspace_users')
      .select('*', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('role', 'admin');

    return {
      total_projects: totalProjects || 0,
      on_track_projects: onTrackProjects || 0,
      at_risk_projects: atRiskProjects || 0,
      off_track_projects: offTrackProjects || 0,
      total_users: totalUsers || 0,
      admin_users: adminUsers || 0
    };
  } catch (error) {
    handleError(error, 'fetch workspace overview');
    return null;
  }
};
